import type { AxiosInstance } from 'axios';

export interface DocScanData {
  first_name: string;
  last_name: string;
  gender: string;
  dob: string; // YYYY-MM-DD
  citizenship: string;
  iin?: string;
  document: {
    number: string;
    expires_at?: string; // YYYY-MM-DD
  };
}

interface DocScanTask {
  id: string;
  status: 'progress' | 'done';
}

export interface DocScanResult {
  id: string;
  status: 'done';
  data?: DocScanData;
  error?: string;
}

function delayMs(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

async function submitRecognize(
  files: File[],
  client: AxiosInstance,
): Promise<DocScanTask> {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  const response = await client.post<DocScanTask>(
    '/doc-ai/recognize',
    formData,
  );
  return response.data;
}

async function pollResult(
  taskId: string,
  client: AxiosInstance,
  signal: AbortSignal,
  intervalMs = 1500,
  timeoutMs = 60_000,
): Promise<DocScanResult> {
  const deadline = Date.now() + timeoutMs;

  async function poll(): Promise<DocScanResult> {
    if (Date.now() >= deadline) {
      throw new Error('Document recognition timed out');
    }
    const { data } = await client.get<DocScanResult>(
      `/doc-ai/recognize/results/${taskId}`,
    );
    if (data.status === 'done') {
      return data;
    }
    await delayMs(intervalMs, signal);
    return poll();
  }

  return poll();
}

export const docScanApi = { submitRecognize, pollResult };
